import { motion } from "framer-motion";
import LottieAnimation from "./LottieAnimation";

const HeroSection = ({ activeTab, setActiveTab, onContactClick }) => {
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const stats = [
    { number: "50,000+", label: "USERS" },
    { number: "1,200+", label: "HEALTH CARE PARTNERS" },
    { number: "700+", label: "MEDICAL EXPERTS" },
    { number: "₹50Mn+", label: "ANNUAL SAVINGS" },
  ];

  const tabs = [
    { id: "individual", label: "Health for Myself" },
    { id: "partner", label: "For Partners" },
    { id: "corporate", label: "For Corporate" },
  ];

  const getAppStoreLink = () => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return "https://apps.apple.com/app/healthismplus/id6444691038";
    }
    return "https://play.google.com/store/apps/details?id=com.healthismplus.customer";
  };

  return (
    <>
      <section
        className="relative mt-8 md:mt-12 lg:mt-16 overflow-hidden"
        data-testid="hero-section"
      >
        <div
          style={{
            background: `
              radial-gradient(800px at 0% 100%, rgba(19,181,142,0.15), transparent 60%),
              radial-gradient(800px at 100% 0%, rgba(106,251,196,0.25), transparent 60%),
              #ffffff
            `,
          }}
          className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.35fr)] gap-8 md:gap-10 lg:gap-12 items-center px-4 sm:px-6 md:px-8 lg:px-14 xl:px-20 py-10 sm:py-12 md:py-16"
        >
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 md:mb-6"
              style={{ background: "#E3F2EE" }}
              data-testid="hero-badge"
            >
              <span className="text-xs sm:text-sm text-[#005143] font-semibold tracking-wide">
                HEALTHCARE REDEFINED
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-[34px] sm:text-[42px] md:text-[50px] lg:text-[56px] font-extrabold text-[#1A1A1A] mb-5 md:mb-6 leading-[1.08]"
              data-testid="hero-heading"
            >
              Your Health.
              <br />
              Your People.
              <br />
              <span className="text-[#006B5A]">One Powerful</span>
              <br />
              <span className="text-[#006B5A]">Platform.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-sm sm:text-base text-[#3D4945] mb-7 md:mb-8 max-w-[520px] mx-auto lg:mx-0 leading-relaxed"
              data-testid="hero-subtext"
              style={{ fontFamily: "Manrope" }}
            >
              Experience the future of healthcare with an all-in-one ecosystem
              designed for preventative wellness, instant care, and AI-driven
              insights.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row sm:flex-wrap justify-center lg:justify-start gap-3 sm:gap-4"
            >
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                href={getAppStoreLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-6 md:px-7 py-3.5 rounded-[14px] font-semibold text-white inline-flex items-center justify-center gap-2 text-sm"
                style={{
                  background:
                    "linear-gradient(90deg, #006B5A 0%, #27A28B 100%)",
                }}
                data-testid="hero-cta-download"
              >
                Download App
              </motion.a>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={onContactClick}
                className="w-full sm:w-auto px-6 md:px-7 py-3.5 rounded-[14px] font-semibold bg-white text-[#006B5A] border border-[#D6E0DC] hover:border-[#13B58E] hover:text-[#13B58E] transition-colors text-sm"
                data-testid="hero-cta-contact"
              >
                Contact Us
              </motion.button>
            </motion.div>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex w-full max-w-[560px] justify-self-center items-end lg:max-w-none"
          >
            <div className="grid w-full grid-cols-1 items-end gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.86fr)] lg:gap-5">
              <div className="relative mx-auto min-h-[190px] w-full max-w-[420px] sm:min-h-[300px] lg:min-h-[520px] lg:-translate-x-6">
                <div className="absolute inset-x-0 bottom-0 flex aspect-[2/1] items-center justify-center overflow-hidden rounded-xl bg-white/70 lg:aspect-[4/3] sm:rounded-2xl">
                  <LottieAnimation
                    path="/animations/fireworks/animations/12345.json"
                    className="h-full w-full"
                  />
                </div>
                <LottieAnimation
                  path="/animations/diwali/animations/12345.json"
                  assetsPath="/animations/diwali/images/"
                  className="absolute left-1/2 top-0 z-10 h-[82%] w-[82%] -translate-x-1/2 lg:h-[62%] lg:w-[62%]"
                />
              </div>
              <img
                src="/assets/hero-image2.png"
                alt="Health Monitoring Dashboard"
                className="mx-auto w-full max-w-[360px] max-h-[550px] self-end object-contain lg:max-w-none"
              />
            </div>
          </motion.div>
        </div>

        {/* Stats Section with Badge - Matching your Image */}
        <div className="bg-[#F8FAFB] py-12 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">

            {/* Pill Badge */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-[#2AA787]/30 bg-white shadow-sm">
                <div className="w-7 h-7 rounded-full bg-[#2AA787] flex items-center justify-center text-white">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                </div>
                <p className="text-[#0B2E2B] text-xs sm:text-sm font-medium">
                  Officially launched by <span className="font-bold underline decoration-[#2AA787] underline-offset-2">Govt. of Maharashtra</span> · Issued to Mumbai Police
                </p>
              </div>
            </div>

            {/* Rounded Stats Box */}
            <div className="bg-white rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.03)] overflow-hidden grid grid-cols-2 md:grid-cols-4">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className={`
                    flex flex-col items-center justify-center
                    px-4 py-8 md:py-12 text-center
                    ${i !== stats.length - 1 ? (i % 2 === 0 ? "border-r border-gray-100 md:border-r" : "md:border-r border-gray-100") : ""}
                    ${i < 2 ? "border-b border-gray-100 md:border-b-0" : ""}
                  `}
                >
                  <div className="text-[28px] md:text-[36px] font-bold text-[#006B5A] leading-none">
                    {stat.number}
                  </div>

                  <div className="text-[10px] sm:text-[11px] text-[#3D4945] mt-2 tracking-wider font-bold">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section
        id="tab-switcher"
        className="px-4 sm:px-6 md:px-8 lg:px-12 bg-[#FFFFFF] py-12 md:py-14 lg:py-16"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-[28px] sm:text-[34px] md:text-[42px] lg:text-[48px] font-extrabold text-[#191C1D] tracking-tight">
            Who Are You Here For?
          </h2>

          <p className="text-[#3D4945] mt-3 mb-8 md:mb-10 text-sm sm:text-[15px] md:text-[16px] max-w-2xl mx-auto">
            Select the journey that best fits your needs to explore tailored
            healthcare solutions.
          </p>

          <div className="flex flex-col sm:flex-wrap sm:flex-row justify-center gap-3 sm:gap-4 md:gap-5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  w-full sm:w-auto
                  px-6 sm:px-10 md:px-14 lg:px-16
                  py-3.5 md:py-4
                  rounded-[20px] md:rounded-[28px]
                  text-sm sm:text-base md:text-lg lg:text-[20px]
                  font-medium border transition-all duration-200
                  ${activeTab === tab.id
                    ? tab.id === "partner"
                      ? "bg-[#2A3077] text-white border-[#2A3077] shadow-md"
                      : "bg-[#2AA787] text-white border-[#2AA787] shadow-md"
                    : tab.id === "partner"
                      ? "bg-[#F5F7F6] text-[#2A3077] border-[#2A3077]"
                      : "bg-[#F5F7F6] text-[#2AA787] border-[#2AA787]"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;